import Cookies from "js-cookie";
import * as Sentry from "@sentry/browser";
import getApiDomain from "@utils/services/apiDomain";
import getAuthCookie from "./authCookieParser";
import { send } from "./fetch";

export class FetchServerContextError extends Error {}

export class FetchInAppServerContextError extends Error {}

/**
 * Validates the server context. Returns false if the context is undefined, empty or does not have the critical properties;
 * @param {object} context
 */
export function isServerContextValid(context) {
  const criticalProperties = ["user_id"];
  if (
    !context ||
    (Object.keys(context).length === 0 && context.constructor === Object)
  ) {
    return false;
  }
  return !criticalProperties.some(
    (property) => !context[property] || context[property] === "undefined"
  );
}

/**
 * Look through serverContext and if certain fields are found, set them in
 * the cookie.
 * @param {Object} serverContext
 */
function setCampaignCookiesAndReturnContext(serverContext) {
  if (
    serverContext.campaign_promo_cookie &&
    serverContext.campaign_promo_cookie.active
  ) {
    Cookies.set("campaign_promo", serverContext.campaign_promo_cookie.value, {
      domain: serverContext.session_cookie_domain,
    });
  } else {
    Cookies.remove("campaign_promo");
  }
  return serverContext;
}

/**
 * Look through meristemContext and if certain fields are found, set them in
 * the cookie.
 */
export function setCookiesFromMeristemContext(meristemContext) {
  if (meristemContext.route_id) {
    Cookies.set("_routeId", meristemContext.route_id, {
      domain: getApiDomain(),
    });
  }

  if (meristemContext.language_code) {
    Cookies.set("_languageCode", meristemContext.language_code, {
      domain: getApiDomain(),
    });
  }
}

/**
 * Performs the fetch request to the appropriate url.
 * @param {string} URL
 */
function fetchContext(URL) {
  return send("GET", URL)
    .then((context) => {
      if (!isServerContextValid(context)) {
        throw Error(`Invalid context: ${JSON.stringify(context)}`);
      }
      return setCampaignCookiesAndReturnContext(context);
    })
    .catch((error) => {
      // Had to add Sentry here too because its initialization is after
      // This can be refactored in the future
      const SENTRY_SAMPLE_RATE = 100;
      Sentry.init({
        dsn: __SENTRY_PROJECT_DSN_JS__,
        shouldSendCallback() {
          const sampleRate = SENTRY_SAMPLE_RATE;
          return Math.random() * 100 <= sampleRate;
        },
      });

      Sentry.withScope((scope) => {
        // NOTE(Paul): change the fingerprint to track it as new sentry error
        scope.setFingerprint([
          "GET",
          "landing page server context fetch detailed",
        ]);
        scope.setExtra("error", error);
        scope.setExtra("URL", error?.request?.url || URL);
        scope.setExtra("Response extra", {
          headers: error.response.headers,
          ok: error.response.statusText === "OK",
          statusText: error.response.statusText,
          status: error.response.status,
        });

        Sentry.captureException(error);
      });
      throw new FetchServerContextError();
    });
}

/**
 * Fetch the serverContext of In-app-upsells from the Growth API.
 */
function fetchInAppContext(URL) {
  const authCookie = getAuthCookie();
  return send("POST", URL, authCookie)
    .then((context) => {
      if (!isServerContextValid(context)) {
        throw Error(`Invalid context: ${JSON.stringify(context)}`);
      }
      return setCampaignCookiesAndReturnContext(context);
    })
    .catch((error) => {
      // Had to add Sentry here too because its initialization is after
      // This can be refactored in the future
      const SENTRY_SAMPLE_RATE = 100;
      Sentry.init({
        dsn: __SENTRY_PROJECT_DSN_JS__,
        shouldSendCallback() {
          const sampleRate = SENTRY_SAMPLE_RATE;
          return Math.random() * 100 <= sampleRate;
        },
      });

      Sentry.withScope((scope) => {
        // NOTE(Paul): change the fingerprint to track it as new sentry error
        scope.setFingerprint([
          "POST",
          "in-app-upsells server context fetch detailed",
        ]);
        scope.setExtra("URL", error?.request?.url || URL);
        scope.setExtra("Response extra", {
          headers: error.response.header,
          ok: error.response.statusText === "OK",
          status: error.response.status,
          statusText: error.response.statusText,
        });
        Sentry.captureException(error);
      });
      throw new FetchInAppServerContextError();
    });
}

/**
 * Fetch the serverContext from the Growth API.
 * Currently has special handling in app context fetching.
 */
export default function getContext() {
  let { pathname } = window.location;
  const { search } = window.location;

  if (pathname.startsWith("/")) {
    pathname = pathname.slice(1, pathname.length);
  }
  if (pathname.endsWith("/")) {
    pathname = pathname.slice(0, pathname.length - 1);
  }

  // Use v2 routing for /add-ons, /offers, and /in-app-offers
  if (
    ["add-ons", "offers", "noom-premium"].some((str) => pathname.includes(str))
  ) {
    pathname += "/v2";
  }

  const pathParts = [];
  if (pathname !== "") {
    pathParts.push(pathname);
  }
  if (search !== "") {
    pathParts.push(search);
  }

  const URL = `/api/context/v2/${pathParts.join("/")}`;

  // NOTE(sumin): Hacky way to call the POST request version only for in-app-offers users.
  if (pathname.includes("in-app-offers") || pathname.includes("course")) {
    return fetchInAppContext(URL);
  }
  return fetchContext(URL);
}
